/**
 * The working paper of a control test run: what the Test Results tabs show, as one JSON document. The PDF and Excel
 * downloads only lay this out, so they match the screen: the same status (the reviewer's over the AI's), the same
 * columns (the downloader's own Columns choices), the same values, currency and wording.
 *
 * `loadWorkingPaperData` fetches what the tabs fetch; `buildWorkingPaperModel` is pure (tested on saved run data).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import axiosInstance from '../../../api/axios';
import { formatCalendarDate } from '../../../utils/calendarDate';
import { getPopulationRequirement } from '../../controls/populationRequirement.ts';
import { resolveExpectedSamples } from '../../../components/test-runs/sampleRows.js';
import { buildLineItemModel, evidenceEntries, lineCellValue } from '../../../components/attribute-result/lineItemModel.js';
import { isLineItemAggregateField, lineLevelFieldValue } from '../../../components/attribute-result/itemFields.js';
import {
  attributeDisplayStatus, attributeFinding, attributeHumanStatus, notCompletedCount,
  reviewerLabel, sampleDisplayStatus, sampleHasReviewerOverride,
} from './status.ts';
import {
  aiExtraColumns, ColumnChoices, lineColumnVisible, loadColumnChoices, loadLineColumnChoices, lookupField,
  pivotSamples, planFieldColumns, resolveColumns, sampleKey, sourceText,
} from './fields.ts';
import {
  ipeConclusionText, populationFacts, populationPeriod, populationSamples, runCurrency, tieOutRows, TieOutRow,
} from './population.ts';
import { conclusionBanner, conclusionTexts, confidencePct, headerConclusion, preparedBy, reviewedBy, reviewProgress } from './header.ts';
import { buildExhibits } from './exhibits.ts';

export const WORKING_PAPER_VERSION = 1;

// ── the model ────────────────────────────────────────────────────────────────────────────────────────────────

export interface WpStatus { value: string; label: string; byReviewer: boolean }

export interface WorkingPaperModel {
  version: number;
  generatedAt: string;
  currency: string | null;
  header: {
    title: string;
    wpRef: string;
    runLabel: string;
    runName: string;
    client: string;
    control: string;
    significance: string;
    frequency: string;
    period: string;
    preparedBy: { name: string; date: string };
    reviewedBy: { name: string; date: string } | null;
    badge: { kind: string; label: string } | null;
    confidence: string | null;
    exceptionsNoted: number;
    reviewed: string;
  };
  objective: {
    controlDescription: string;
    testingObjective: string;
    assertions: string;
    approach: string;
    externalReliance: string;
    controlNature: string;
  };
  workPerformed: Array<{ ref: string; label: string; description: string; status: WpStatus; steps: string; stepsByReviewer: boolean }>;
  conclusion: {
    banner: { kind: string; label: string };
    text: string;
    textByReviewer: boolean;
    keyFindings: string[];
    keyFindingsByReviewer: boolean;
    recommendations: string[];
    recommendationsByReviewer: boolean;
  };
  exhibits: Array<{ label: string; file: string; type: string; attributes: string[]; status: string }>;
  population: {
    notRequired: boolean;
    rationale: string | null;
    meta: Array<{ label: string; value: string }>;
    tieOut: null | {
      heading: string;
      verified: boolean;
      rows: TieOutRow[];
      populationFile: string;
      proofFile: string;
      sourceNote: string;
    };
    conclusion: { text: string; byReviewer: boolean } | null;
    notes: string | null;
    samples: { columns: string[]; rows: string[][]; footnote: string };
  };
  samples: {
    attributes: Array<{
      ref: string; label: string; status: WpStatus; finding: string; findingByReviewer: boolean;
      observations: string; notCompleted: number;
    }>;
    sampleColumns: Array<{ id: string; label: string; source: string }>;
    lineColumns: Array<{ id: string; label: string }>;
    hiddenAiFields: string[];
    rows: Array<{
      n: number;
      sample: string;
      sampleValues: string[];
      lineValues: string[] | null;            // null: no line items for this sample
      lineLocation: string | null;            // where the line was read (file, page / row)
      evidence: Array<{ file: string; text: string; sub: string }>;
      results: Array<{ ref: string; status: string; mark: string; reviewer: string | null }>;
      reviewerOverride: boolean;
      note: string;
    }>;
    reviewNotes: Array<{ sample: string; attribute: string; note: string }>;
    sampleResults: Array<{
      sample: string; attribute: string; status: WpStatus; observation: string; rationale: string;
      excluded: boolean; exclusionReason: string; notCompleted: boolean;
    }>;
  };
  issues: Array<{
    n: number; attribute: string; sample: string; description: string; severity: string; disposition: string;
    rootCause: string; remediation: string; owner: string; managementResponse: string; falsePositive: boolean;
    falsePositiveReason: string;
  }>;
  signOff: {
    status: string; conclusion: string; preparer: string; preparedAt: string; reviewer: string; signedOffAt: string;
    reviewNotes: string;
  };
}

export interface WorkingPaperData {
  runId: string;
  run: any;
  attributes: any[];
  evidenceMap: any[];
  control: any;
  planFields: any[];        // the run's plan snapshot (else the live plan)
  planAttributes: any[];
  livePlanFields: any[];
  exceptions: any[];
}

export interface BuildOptions {
  /** The downloader's column choices (Samples Testing → Columns); default: saved in this browser. */
  columnChoices?: ColumnChoices;
  lineColumnChoices?: Record<string, boolean>;
  now?: Date;
}

// ── formatting ───────────────────────────────────────────────────────────────────────────────────────────────

const calDate = (v: any) => formatCalendarDate(v) || '—';
const instDate = (v: any) => (v ? new Date(v).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const text = (v: any) => (v == null || v === '' ? '' : String(v));
const dash = (v: any) => (v == null || v === '' ? '—' : String(v));
const stripQuotes = (r: any) => (typeof r === 'string' ? r.replace(/^["']+|["']+$/g, '').trim() : r);

const ATTR_STATUS_LABEL: Record<string, string> = { passed: 'Pass', failed: 'Exception', warning: 'Inconclusive' };
const SAMPLE_STATUS_LABEL: Record<string, string> = {
  passed: 'Pass', failed: 'Exception', exception: 'Exception', warning: 'Inconclusive', n_a: 'N/A',
};
/** The line result marks of the Samples Testing table. */
export const RESULT_MARK: Record<string, string> = { passed: '✓', failed: '✗', warning: '!', skipped: '–', incomplete: '↻' };

const ASSERTION_LABEL: Record<string, string> = {
  existence: 'Existence', 'accuracy & valuation': 'Accuracy & Valuation', occurence: 'Occurence',
  'rights & obligation': 'Rights & Obligation', 'cut-off': 'Cut-off',
  'presentation & disclosure': 'Presentation & Disclosure', completeness: 'Completeness',
};

const SELECTION_FOOTNOTE = (notRequired: boolean, label: string, n: number) => (notRequired
  ? `^ Samples are groups of evidence files chosen by the auditor (no population). Evidence files correspond to Exhibits E-1 through E-${n}.`
  : `^ Items selected via ${label.toLowerCase()} from the population. Evidence files correspond to Exhibits E-1 through E-${n}.`);

// ── build ────────────────────────────────────────────────────────────────────────────────────────────────────

export function buildWorkingPaperModel(data: WorkingPaperData, opts: BuildOptions = {}): WorkingPaperModel {
  const { runId, run = {}, attributes = [], evidenceMap = [], control = {}, planAttributes = [], livePlanFields = [], exceptions = [] } = data;
  const planFields = data.planFields?.length ? data.planFields : livePlanFields;
  const exec = run.execution || {};
  const currency = runCurrency(run);

  // Header
  const badge = headerConclusion(exec, run);
  const progress = reviewProgress(attributes);
  const conf = confidencePct(exec);
  const prep = preparedBy(run, attributes[0]?.preparer_info);
  const rev = reviewedBy(run);
  const header = {
    title: 'Working Paper — SOX Control Test',
    wpRef: control.control_id ? `${control.control_id}-OE` : '',
    runLabel: run.run_number != null ? `RUN-${run.run_number}` : '',
    runName: text(run.test_run_name),
    client: 'Skeptics Inc.',
    control: [control.control_id, control.title || control.control_wording_text].filter(Boolean).join(' — '),
    significance: dash(control.significance),
    frequency: dash(control.frequency),
    period: `${calDate(run.testing_period_start)} — ${calDate(run.testing_period_end)}`,
    preparedBy: { name: prep.aiAssisted ? `${prep.name} (AI-Assisted)` : prep.name, date: instDate(run.created_at) },
    reviewedBy: rev ? { name: rev.name, date: rev.at ? instDate(rev.at) : '' } : null,
    badge,
    confidence: conf != null ? `AI confidence ${conf}%` : null,
    exceptionsNoted: Number(exec.total_exceptions || 0),
    reviewed: `${progress.reviewed} / ${progress.total} reviewed`,
  };

  // I. Objective
  const periodStart = run.testing_period_start ? calDate(run.testing_period_start) : null;
  const periodEnd = run.testing_period_end ? calDate(run.testing_period_end) : null;
  const periodText = periodStart && periodEnd ? `${periodStart} to ${periodEnd}` : 'the period under review';
  const rawAssertions = control.assertions;
  const objective = {
    controlDescription: dash(control.objective || control.control_wording_text),
    testingObjective: `To test the operating effectiveness of this control during the period ${periodText}, by evaluating whether the control was applied consistently and completely in accordance with its stated design for each item in the sample.`,
    assertions: Array.isArray(rawAssertions) && rawAssertions.length
      ? rawAssertions.map((v: string) => ASSERTION_LABEL[v] || v).join(', ')
      : (typeof rawAssertions === 'string' && rawAssertions.trim() ? rawAssertions : '—'),
    approach: 'Attribute-based sample testing',
    externalReliance: control.external_auditor_reliance ? 'Yes — reliance placed' : 'No',
    controlNature: dash(control.control_nature),
  };

  // II. Work performed
  const planStepsByRef: Record<string, any[]> = {};
  planAttributes.forEach((pa: any) => { const k = pa.display_ref || pa.id; if (k) planStepsByRef[k] = pa.steps || []; });
  const attrStatus = (a: any): WpStatus => {
    const value = attributeDisplayStatus(a);
    return { value, label: ATTR_STATUS_LABEL[value] || (value ? value : '—'), byReviewer: !!attributeHumanStatus(a) };
  };
  const workPerformed = attributes.map((a: any) => {
    const ref = a.attribute_ref || '?';
    const steps = (planStepsByRef[ref]?.length ? planStepsByRef[ref] : Array.isArray(a.steps_snapshot) ? a.steps_snapshot : []);
    const aiSteps = steps.map((s: any, i: number) => `${s.step_number ?? i + 1}. ${s.instruction || s.description || s.audit_procedure || ''}`.trim()).join('\n\n');
    return {
      ref, label: dash(a.label), description: text(a.description), status: attrStatus(a),
      steps: a.human_steps_text || aiSteps, stepsByReviewer: !!a.human_steps_text,
    };
  });

  // III. Conclusion
  const ct = conclusionTexts(run, exec);
  const conclusion = {
    banner: conclusionBanner(exec),
    text: ct.conclusion, textByReviewer: ct.conclusionByReviewer,
    keyFindings: ct.keyFindings, keyFindingsByReviewer: ct.keyFindingsByReviewer,
    recommendations: ct.recommendations, recommendationsByReviewer: ct.recommendationsByReviewer,
  };

  // IV. Exhibits
  const ex = buildExhibits(evidenceMap, attributes, run);
  const exhibits = [...ex.population, ...ex.files].map((e) => ({
    label: e.label, file: e.file, type: e.type || 'Evidence', attributes: e.attributes, status: e.status,
  }));

  // Population
  const facts = populationFacts(run);
  const period = populationPeriod(run);
  const population = run.population || {};
  const ps = populationSamples(run, evidenceMap, attributes);
  const totalPop = population.human_total_count ?? facts.totalPopulation;
  const meta = [
    { label: 'Total population', value: totalPop != null ? `${Number(totalPop).toLocaleString()} items` : '—' },
    { label: 'Period start', value: dash(population.human_testing_period_start || period.start) },
    { label: 'Period end', value: dash(population.human_testing_period_end || period.end) },
    { label: 'Sample size', value: `${ps.refs.length} item${ps.refs.length !== 1 ? 's' : ''}` },
    { label: 'Selection method', value: facts.selectionText },
    ...(!facts.notRequired && facts.identifierColumn ? [{ label: 'Identifier column', value: String(facts.identifierColumn) }] : []),
    ...(!facts.notRequired ? [{ label: 'Population completeness', value: facts.isVerified ? '✓ Verified' : facts.isException ? '⚠ Exception' : 'Pending verification' }] : []),
  ];
  const aiIpeConclusion = ipeConclusionText(facts, period, currency);
  const popFields = planFields.length > 0 ? planFields : [];
  const populationModel = {
    notRequired: facts.notRequired,
    rationale: facts.notRequired ? (getPopulationRequirement(control).rationale || null) : null,
    meta,
    tieOut: facts.hasIPEData ? {
      heading: facts.isVerified ? 'IPE Tied Out — Population Verified' : 'IPE Exception — Discrepancy Exceeds Tolerance',
      verified: facts.isVerified,
      rows: tieOutRows(run, facts, period, currency),
      populationFile: dash(facts.popFileName),
      proofFile: dash(facts.proofFileName),
      sourceNote: population.human_ipe_source_note || facts.ipeSourceNote || 'Not specified',
    } : null,
    conclusion: aiIpeConclusion || population.human_ipe_conclusion
      ? { text: population.human_ipe_conclusion || aiIpeConclusion || '', byReviewer: !!population.human_ipe_conclusion }
      : null,
    notes: population.notes || null,
    samples: {
      columns: ['Item', 'Sample Ref', ...popFields.map((f: any) => f.label || f.field_key || ''), 'Evidence File'],
      rows: ps.refs.map((ref, i) => [
        String(i + 1), String(ref),
        ...popFields.map((f: any) => dash(ps.fieldData[ps.key(ref)]?.[f.label] || ps.fieldData[ps.key(ref)]?.[f.field_key])),
        dash(ps.sampleToFile[ps.key(ref)]),
      ]),
      footnote: SELECTION_FOOTNOTE(facts.notRequired, facts.selectionLabel || 'the population', ps.refs.length),
    },
  };

  // Samples Testing
  const expected = resolveExpectedSamples(run, evidenceMap);
  const pivot = pivotSamples(attributes, expected);
  const planCols = planFieldColumns(planFields, livePlanFields, pivot.rows, pivot.aiFieldMeta);
  const extraCols = aiExtraColumns(planFields, planCols, pivot.rows, pivot.aiFieldMeta);
  const choices = opts.columnChoices || loadColumnChoices(runId);
  const cols = resolveColumns(planCols, extraCols, choices, pivot.aiFieldMeta);
  const sampleCols = cols.allFields.filter((f) => !isLineItemAggregateField(f) && !cols.effectiveHiddenIds.includes(f.id));
  const attrCols = attributes.map((a: any) => ({ id: a.attribute_id, ref: a.attribute_ref || '?', label: a.label || '' }));
  const lim = buildLineItemModel(pivot.rows, attrCols, evidenceMap);
  const lineChoices = opts.lineColumnChoices || loadLineColumnChoices(runId);
  const lineCols = lim.lineCols.filter((c: any) => lineColumnVisible(lineChoices, c));

  const rows = lim.lines.map((ln: any, idx: number) => {
    const failed = ln.results.find((r: any) => r.status === 'failed');
    const noteSrc = failed || ln.results[0];
    const results = ln.results.map((r: any) => {
      const human = sampleHasReviewerOverride(r.cell);
      return {
        ref: r.attr.ref, status: r.status || '', mark: RESULT_MARK[r.status] || '–',
        reviewer: human ? reviewerLabel(sampleDisplayStatus(r.cell)) : null,
      };
    });
    const loc = ln.item?.source?.location;
    return {
      n: idx + 1,
      sample: ln.sampleRef,
      sampleValues: sampleCols.map((f) => {
        const own = ln.siblings > 1 ? lineLevelFieldValue(f, ln.item) : null;
        return dash(own !== null ? own : lookupField(ln.data._fieldData || {}, f));
      }),
      lineValues: ln.item ? lineCols.map((c: any) => dash(lineCellValue(c, ln.item, currency))) : null,
      lineLocation: loc?.file
        ? `${loc.file}${loc.page_number ? ` p${loc.page_number}` : ''}${loc.row_number && !loc.page_number ? ` row ${loc.row_number}` : ''}`
        : null,
      evidence: evidenceEntries(ln, currency).map((e: any) => ({ file: e.file, text: text(e.text), sub: text(e.sub) })),
      results,
      reviewerOverride: results.some((r: { reviewer: string | null }) => !!r.reviewer),
      note: text(noteSrc?.item?.observation || noteSrc?.cell?.observation),
    };
  });

  // Sample names as the table shows them (the stored refs can be lower case), in its order.
  const rowLabel = new Map<string, string>(pivot.rows.map(([label]) => [sampleKey(label), String(label)]));
  const rowOrder = new Map<string, number>(pivot.rows.map(([label], i) => [sampleKey(label), i]));
  const sampleResults = attributes.flatMap((a: any) => (a.sample_results || []).map((sr: any) => {
    const cell = { status: sr.status, aiResult: sr.ai_result || sr.status, humanResult: sr.human_result, finalResult: sr.final_result };
    const value = sampleDisplayStatus(cell) || '';
    return {
      sample: rowLabel.get(sampleKey(sr.record_ref)) || stripQuotes(sr.record_ref), attribute: a.attribute_ref || '?',
      _order: rowOrder.get(sampleKey(sr.record_ref)) ?? Number.MAX_SAFE_INTEGER,
      status: { value, label: SAMPLE_STATUS_LABEL[value] || value || '—', byReviewer: sampleHasReviewerOverride(cell) },
      observation: text(sr.observation), rationale: text(sr.human_rationale),
      excluded: !!sr.excluded, exclusionReason: text(sr.exclusion_reason), notCompleted: !!sr.attributes?._incomplete,
    };
  })).sort((x: any, y: any) => x._order - y._order || x.attribute.localeCompare(y.attribute))
    .map(({ _order, ...r }: any) => r);

  const samples = {
    attributes: attributes.map((a: any) => {
      const f = attributeFinding(a);
      return {
        ref: a.attribute_ref || '?', label: text(a.label), status: attrStatus(a),
        finding: f.shown, findingByReviewer: !!f.human, observations: f.observations, notCompleted: notCompletedCount(a),
      };
    }),
    sampleColumns: sampleCols.map((f) => ({ id: f.id, label: String(f._liveLabel || f.label || f.field_key || ''), source: sourceText(pivot.fieldSources, f) })),
    lineColumns: lineCols.map((c: any) => ({ id: c.id, label: c.label })),
    hiddenAiFields: cols.hiddenExtras.map((f) => String(f.label)),
    rows,
    reviewNotes: pivot.reviewNotes.map((n) => ({ sample: n.sample, attribute: `${n.attrRef}${n.attrLabel ? ` ${n.attrLabel}` : ''}`, note: n.note })),
    sampleResults,
  };

  // Issues (the run's exceptions)
  const issues = exceptions.map((e: any, i: number) => ({
    n: i + 1,
    attribute: [e.attribute_ref, e.attribute_label].filter(Boolean).join(' — ') || '—',
    sample: dash(e.record_ref),
    description: dash(e.description),
    severity: dash(e.severity),
    disposition: dash(e.disposition),
    rootCause: text(e.root_cause), remediation: text(e.remediation), owner: text(e.owner),
    managementResponse: text(e.management_response),
    falsePositive: !!e.false_positive, falsePositiveReason: text(e.false_positive_reason),
  }));

  // Sign-off
  const signOff = {
    status: dash(run.workflow_status),
    conclusion: dash(run.conclusion),
    preparer: dash(run.preparer_name),
    preparedAt: run.prepared_at ? instDate(run.prepared_at) : '—',
    reviewer: dash(run.reviewer_name || run.signed_off_by),
    signedOffAt: run.signed_off_at ? instDate(run.signed_off_at) : '—',
    reviewNotes: text(run.review_notes),
  };

  return {
    version: WORKING_PAPER_VERSION,
    generatedAt: (opts.now || new Date()).toISOString(),
    currency,
    header, objective, workPerformed, conclusion, exhibits,
    population: populationModel,
    samples, issues, signOff,
  };
}

// ── load ─────────────────────────────────────────────────────────────────────────────────────────────────────

/** What the calling screen has already loaded and shows: used as it is instead of being loaded again, so the
 * working paper has the plan columns the screen shows (the results page shows the live plan for the latest run)
 * and no extra request is made for the control. */
export interface ScreenData {
  control?: any;
  planFields?: any[];
  livePlanFields?: any[];
  planAttributes?: any[];
}

/** What the Test Results tabs load for a run. */
export async function loadWorkingPaperData(runId: string, screen: ScreenData = {}): Promise<WorkingPaperData> {
  const hasPlan = Array.isArray(screen.planFields);
  const [runResp, emResp, planResp, excResp] = await Promise.all([
    axiosInstance.get(`/api/control-test-runs/${runId}`),
    axiosInstance.get(`/api/control-test-runs/${runId}/evidence-map`).catch(() => ({ data: {} })),
    hasPlan ? Promise.resolve({ data: {} }) : axiosInstance.get(`/api/control-test-runs/${runId}/plan`).catch(() => ({ data: {} })),
    axiosInstance.get(`/api/control-test-runs/${runId}/exceptions`).catch(() => ({ data: {} })),
  ]);
  const run = runResp.data?.run || runResp.data || {};
  const em = emResp.data;
  const evidenceMap = Array.isArray(em) ? em : (em?.cells || em?.evidence_map || []);
  let control: any = screen.control || {};
  let livePlanFields: any[] = screen.livePlanFields || [];
  if (run.sox_control_id && (!screen.control || !hasPlan)) {
    try {
      if (!screen.control) control = (await axiosInstance.get(`/api/sox-controls/${run.sox_control_id}`)).data || {};
      if (!hasPlan) {
        const live = await axiosInstance.get(`/api/sox-controls/${control.id || run.sox_control_id}/test-plan`);
        livePlanFields = live.data?.fields || [];
      }
    } catch { /* the header then shows what the run has */ }
  }
  return {
    runId,
    run,
    attributes: runResp.data?.attributes || [],
    evidenceMap,
    control,
    planFields: hasPlan ? screen.planFields! : (planResp.data?.fields || planResp.data?.plan_fields || []),
    planAttributes: hasPlan ? (screen.planAttributes || []) : (planResp.data?.attributes || planResp.data?.plan_attributes || []),
    livePlanFields,
    exceptions: excResp.data?.exceptions || [],
  };
}

/** The working paper of a run, as it shows on this screen for this user. */
export async function createWorkingPaper(runId: string, opts: BuildOptions = {}, screen: ScreenData = {}): Promise<WorkingPaperModel> {
  return buildWorkingPaperModel(await loadWorkingPaperData(runId, screen), opts);
}

